import React, { useState } from 'react';
import './contact.css';

const Contact = () => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  // Error and success message states
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setErrorMessage('Please fill out all fields.');
      setShowError(true);
      setShowSuccess(false);
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage('Please enter a valid email address.');
      setShowError(true);
      setShowSuccess(false);
      return;
    }
    
    // Submit to Web3Forms
    setIsSubmitting(true);
    
    try {
      // Use your Web3Forms access key
      const accessKey = "b022b84a-dd6a-44dc-bdb1-8031e9dae7fd";
      
      const payload = {
        ...formData,
        access_key: accessKey,
        from_name: "Portfolio Contact Form",
        subject: `New Message from ${formData.name}: ${formData.subject}`
      };

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        
        // Show success message
        setShowError(false);
        setSuccessMessage('Your message has been sent successfully! I will get back to you shortly.');
        setShowSuccess(true);
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      } else {
        throw new Error(result.message || 'Form submission failed');
      }
    } catch (error) {
      setErrorMessage('There was a problem sending your message. Please try again later.');
      setShowError(true);
      setShowSuccess(false);
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact">
      <div className="container">
        <div className="center-wrapper">
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">
            Have a project in mind or want to discuss a potential collaboration? I'd love to hear from you!
          </p>
        </div>
        
        <div className="contact-container">
          <div className="contact-info">
            {/* Decorative elements */}
            <div className="floating-element circle" style={{width: '200px', height: '200px', top: '-50px', right: '-30px', backgroundColor: 'var(--secondary)', zIndex: '-1'}}></div>
            <div className="floating-element triangle" style={{width: '150px', height: '150px', bottom: '20px', left: '-40px', backgroundColor: 'var(--tertiary)', zIndex: '-1'}}></div>
            
            <h3 className="contact-header">Let's Build Something Amazing Together</h3>
            <p className="contact-description">
              I'm currently open to freelance projects, full-time positions, and collaborative opportunities in web development, AI solutions, and no-code implementations.
            </p>
            
            <div className="contact-methods">
              <div className="contact-method">
                <div className="method-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                  </svg>
                </div>
                <div className="method-content">
                  <div className="method-label">Email</div>
                  <div className="method-value">mail@richardfernando.com</div>
                </div>
              </div>
              
              <div className="contact-method">
                <div className="method-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                  </svg>
                </div>
                <div className="method-content">
                  <div className="method-label">Phone</div>
                  <div className="method-value">+91-9967552082</div>
                </div>
              </div>
              
              <div className="contact-method">
                <div className="method-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                </div>
                <div className="method-content">
                  <div className="method-label">Location</div>
                  <div className="method-value">Mumbai, India</div>
                </div>
              </div>
            </div>
            
            <div className="social-links">
              <a href="https://www.linkedin.com/in/richardfernand0/" className="social-link">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-2 16h-2v-6h2v6zm-1-6.891c-.607 0-1.1-.496-1.1-1.109 0-.612.492-1.109 1.1-1.109s1.1.497 1.1 1.109c0 .613-.493 1.109-1.1 1.109zm8 6.891h-1.998v-2.861c0-1.881-2.002-1.722-2.002 0v2.861h-2v-6h2v1.093c.872-1.616 4-1.736 4 1.548v3.359z"/>
                </svg>
              </a>
              <a href="https://github.com/richardfernand0" className="social-link">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>
              {/* <a href="#" className="social-link">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                </svg>
              </a> */}
            </div>
          </div>
          
          <div className="contact-form-container">
            <h3 className="form-header">Send Me a Message</h3>
            <form onSubmit={handleSubmit} id="contact-form">
              <div className="form-group">
                <input 
                  type="text" 
                  className="form-control" 
                  id="name" 
                  name="name" 
                  placeholder=" " 
                  value={formData.name}
                  onChange={handleChange}
                />
                <label htmlFor="name" className="form-label">Your Name</label>
              </div>
              
              <div className="form-group">
                <input 
                  type="email" 
                  className="form-control" 
                  id="email" 
                  name="email" 
                  placeholder=" " 
                  value={formData.email}
                  onChange={handleChange}
                />
                <label htmlFor="email" className="form-label">Your Email</label>
              </div>
              
              <div className="form-group">
                <input 
                  type="text" 
                  className="form-control" 
                  id="subject" 
                  name="subject" 
                  placeholder=" " 
                  value={formData.subject}
                  onChange={handleChange}
                />
                <label htmlFor="subject" className="form-label">Subject</label>
              </div>
              
              <div className="form-group">
                <textarea 
                  className="form-control" 
                  id="message" 
                  name="message" 
                  placeholder=" " 
                  value={formData.message}
                  onChange={handleChange}
                ></textarea>
                <label htmlFor="message" className="form-label">Your Message</label>
              </div>
              
              <button 
                type="submit" 
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              
              {showError && (
                <div className="form-feedback error-message">{errorMessage}</div>
              )}
              
              {showSuccess && (
                <div className="form-feedback success-message">{successMessage}</div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;